import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  issueShortShareToken,
  pickShareAlias,
  shortShareTokenQueryKey,
  type ShortShareTokenRequest,
} from '@/hooks/queries/share/useShortShareToken';
import { mapWithConcurrency } from '@/utils/concurrency';

/** 한 번에 던지는 발급 요청 수. 고객 수백 명이어도 서버를 몰아치지 않게 제한한다. */
const ISSUE_CONCURRENCY = 8;

export const shortShareTokenCacheKey = ({
  targetType,
  hash,
}: ShortShareTokenRequest) => `${targetType}:${hash}`;

/**
 * 여러 공유 대상의 단축 토큰을 필요할 때 한꺼번에 발급받는다.
 *
 * CSV 내보내기처럼 행이 많은 화면은 렌더 시점에 발급하면 페이지를 열 때마다
 * 요청이 쏟아지므로, 내보내기 버튼을 눌렀을 때만 발급한다.
 * 실패한 대상은 토큰 없이 남겨 호출부가 기존 긴 URL로 폴백하게 한다.
 */
const useShortShareTokenBatch = () => {
  const queryClient = useQueryClient();
  /* 이름은 tokens지만 담기는 값은 `/s/` 뒤에 붙일 별칭이다(커스텀 주소가 있으면 그것). */
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [isIssuing, setIsIssuing] = useState(false);

  const issueTokens = useCallback(
    async (requests: ShortShareTokenRequest[]) => {
      const pending = requests.filter(({ hash }) => Boolean(hash));

      setIsIssuing(true);

      try {
        const issued = await mapWithConcurrency(
          pending,
          ISSUE_CONCURRENCY,
          async (request) => {
            try {
              /* 발급 API는 멱등이라 캐시를 그대로 공유한다.
                 이미 다른 화면에서 받아둔 토큰이면 요청이 나가지 않는다. */
              const response = await queryClient.fetchQuery(
                shortShareTokenQueryKey(request),
                () => issueShortShareToken(request),
                { staleTime: Infinity, cacheTime: Infinity },
              );

              return [
                shortShareTokenCacheKey(request),
                pickShareAlias(response.data.data),
              ] as const;
            } catch {
              return null;
            }
          },
        );

        setTokens((prev) => ({
          ...prev,
          ...Object.fromEntries(
            issued.filter((entry): entry is [string, string] => entry !== null),
          ),
        }));
      } finally {
        setIsIssuing(false);
      }
    },
    [queryClient],
  );

  return { tokens, issueTokens, isIssuing };
};

export default useShortShareTokenBatch;
